import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Tag, 
  Calendar,
  DollarSign,
  FileText,
  Upload,
  XCircle,
  Camera,
  QrCode,
  Clock,
  Wrench,
  AlertTriangle,
  Truck,
  Shield,
  Clipboard,
  BarChart2,
  ChevronRight,
  InfoIcon,
  Printer,
  AlertCircle,
  Scan
} from 'lucide-react';
import { QRCode } from 'react-qr-code';
import { ASSET_CATEGORIES, ASSET_STATUSES, LOCATIONS } from '../data/mockData';

const AddAssetForm = () => {
  const navigate = useNavigate();
  const [generatedAssetId, setGeneratedAssetId] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [scanTarget, setScanTarget] = useState('');
  const [formData, setFormData] = useState({
    // Basic Information
    prefix: 'AST',
    suffix: '',
    name: '',
    type: '',
    category: '',
    model: '',
    serialNumber: '',
    manufacturer: '',
    description: '',
    status: 'active',

    // Purchase Information
    purchaseInformation: {
      purchaseDate: '',
      purchasePrice: '',
      purchaseOrder: '',
      invoiceNumber: '',
      vendor: {
        name: '',
        contactPerson: '',
        phone: '',
        email: '',
        vendorId: ''
      },
      purchasedBy: {
        name: '',
        employeeId: '',
        department: ''
      }
    },

    // Location & Assignment
    location: '',
    department: '',
    building: '',
    floor: '',
    room: '',
    assignedTo: '',
    assignmentType: 'location', // location or user

    // Technical Specifications
    specifications: {
      dimensions: '',
      weight: '',
      power: '',
      capacity: '',
      operatingSystem: '',
      customFields: []
    },

    // Maintenance & Service
    maintenanceFrequency: 'quarterly',
    lastMaintenanceDate: '',
    nextMaintenanceDate: '',
    serviceProvider: '',
    serviceContract: '',
    criticalityLevel: 'medium',

    // Financial Information
    depreciationMethod: 'straight-line',
    usefulLife: '',
    salvageValue: '',
    currentValue: '',
    costCenter: '',
    budgetCode: '',

    // Compliance & Safety
    certifications: [],
    safetyRequirements: [],
    complianceStatus: 'compliant',
    lastInspectionDate: '',
    nextInspectionDate: '',

    // Documents & Media
    documents: [],
    images: [],
    manuals: [],
    certificates: [],

    // Add Warranty Information
    warranty: {
      warrantyType: 'manufacturer', // manufacturer, extended, none
      startDate: '',
      endDate: '',
      duration: '', // e.g., "2 years"
      provider: '',
      warrantyNumber: '',
      coverage: '', // full, parts-only, labor-only
      terms: '',
      status: 'active', // active, expired, pending
      documents: [],
      claims: [],
      notes: '',
      extendedWarrantyOption: false,
      extendedWarrantyCost: '',
      registrationStatus: false,
      registrationDate: ''
    }
  });

  // Generate Asset Number
  const generateAssetNumber = () => {
    const timestamp = new Date().getTime().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const assetNumber = `${formData.prefix}-${timestamp}-${random}${formData.suffix ? `-${formData.suffix}` : ''}`;
    setGeneratedAssetId(assetNumber);
  };

  // QR Code Data
  const getQRCodeData = () => {
    return JSON.stringify({
      id: generatedAssetId,
      name: formData.name,
      category: formData.category,
      location: formData.location,
      serialNumber: formData.serialNumber
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Mock API call - replace with actual API
      console.log('Submitting asset:', {
        ...formData,
        assetId: generatedAssetId
      });
      navigate('/assets');
    } catch (error) {
      console.error('Error adding asset:', error);
    }
  };

  const handleFileChange = (e, type) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], ...files]
    }));
  };

  const ScannerModal = ({ isOpen, onClose, onScan, target }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Scan {target === 'model' ? 'Model Number' : 'Serial Number'}
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="p-4">
            <div className="aspect-square bg-gray-100 rounded-lg mb-4">
              <div className="w-full h-full flex items-center justify-center">
                <Scan className="w-12 h-12 text-gray-400" />
              </div>
            </div>
            
            <div className="text-center text-sm text-gray-500">
              Position the QR code or barcode within the frame to scan
            </div>
          </div>

          <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white 
                       border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const manualEntry = prompt('Enter the number manually:');
                if (manualEntry) {
                  onScan(manualEntry);
                }
              }}
              className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-200 
                       rounded-lg hover:bg-blue-50"
            >
              Manual Entry
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header Section - Updated to match AssetsDashboard */}
      <div className="bg-[#43302b] rounded-xl p-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold">Add New Asset</h1>
            <p className="text-brown-100 mt-1">Create and configure a new asset in the system</p>
          </div>
          
          {/* Asset Number Generator */}
          <div className="flex items-center gap-4">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <QrCode className="w-5 h-5 text-white" />
                <div>
                  <p className="text-sm text-white/80">Asset ID</p>
                  <p className="text-lg font-mono font-bold">{generatedAssetId || 'Not Generated'}</p>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={generateAssetNumber}
              className="px-4 py-2 bg-white text-[#43302b] rounded-lg hover:bg-brown-50 
                       transition-colors flex items-center gap-2 font-medium"
            >
              Generate ID
            </button>
          </div>
        </div>

        {/* Asset QR Code Preview */}
        {generatedAssetId && (
          <div className="mt-6 flex items-center gap-6">
            <div className="bg-white p-4 rounded-lg">
              <QRCode value={getQRCodeData()} size={100} />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Asset QR Code</h3>
              <p className="text-brown-100">Scan to view asset details and history</p>
            </div>
          </div>
        )}
      </div>

      {/* Main Form Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="col-span-2 space-y-6">
          {/* Basic Information Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-2 bg-indigo-50 rounded-lg">
                  <Tag className="w-5 h-5 text-indigo-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Asset Number Configuration - Updated */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Prefix</label>
                  <input
                    type="text"
                    value={formData.prefix}
                    onChange={(e) => setFormData(prev => ({ ...prev, prefix: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                    placeholder="AST"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Suffix (Optional)</label>
                  <input
                    type="text"
                    value={formData.suffix}
                    onChange={(e) => setFormData(prev => ({ ...prev, suffix: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                    placeholder="LOC1"
                  />
                </div>

                {/* Continue with other basic fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Asset Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Select Category</option>
                    {ASSET_CATEGORIES.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Model Number</label>
                  <div className="mt-1 flex gap-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={formData.model}
                        onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2
                                 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setScanTarget('model');
                        setShowScanner(true);
                      }}
                      className="px-3 py-2 text-sm text-blue-600 border border-blue-200 
                               hover:bg-blue-50 rounded-lg flex items-center gap-2"
                    >
                      <Scan className="w-4 h-4" />
                      Scan
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Serial Number</label>
                  <div className="mt-1 flex gap-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={formData.serialNumber}
                        onChange={(e) => setFormData(prev => ({ ...prev, serialNumber: e.target.value }))}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2
                                 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setScanTarget('serial');
                        setShowScanner(true);
                      }}
                      className="px-3 py-2 text-sm text-blue-600 border border-blue-200 
                               hover:bg-blue-50 rounded-lg flex items-center gap-2"
                    >
                      <Scan className="w-4 h-4" />
                      Scan
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Purchase & Warranty Information Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Purchase & Warranty Information</h2>
              </div>

              {/* Purchase Details */}
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Purchase Date</label>
                    <input
                      type="date"
                      value={formData.purchaseInformation.purchaseDate}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        purchaseInformation: {
                          ...prev.purchaseInformation,
                          purchaseDate: e.target.value
                        }
                      }))}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                               focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Purchase Price</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        value={formData.purchaseInformation.purchasePrice}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          purchaseInformation: {
                            ...prev.purchaseInformation,
                            purchasePrice: e.target.value
                          }
                        }))}
                        className="block w-full rounded-md border border-gray-300 pl-7 pr-12 py-2
                                 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                {/* Vendor Information */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-4">Vendor Information</h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Vendor Name</label>
                      <input
                        type="text"
                        value={formData.purchaseInformation.vendor.name}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          purchaseInformation: {
                            ...prev.purchaseInformation,
                            vendor: {
                              ...prev.purchaseInformation.vendor,
                              name: e.target.value
                            }
                          }
                        }))}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                                 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Contact Person</label>
                      <input
                        type="text"
                        value={formData.purchaseInformation.vendor.contactPerson}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          purchaseInformation: {
                            ...prev.purchaseInformation,
                            vendor: {
                              ...prev.purchaseInformation.vendor,
                              contactPerson: e.target.value
                            }
                          }
                        }))}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                                 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Warranty Information */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-4">Warranty Details</h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Warranty Type</label>
                      <select
                        value={formData.warranty.warrantyType}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          warranty: {
                            ...prev.warranty,
                            warrantyType: e.target.value
                          }
                        }))}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                                 focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="manufacturer">Manufacturer Warranty</option>
                        <option value="extended">Extended Warranty</option>
                        <option value="none">No Warranty</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Duration</label>
                      <input
                        type="text"
                        value={formData.warranty.duration}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          warranty: {
                            ...prev.warranty,
                            duration: e.target.value
                          }
                        }))}
                        placeholder="e.g., 2 years"
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                                 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Start Date</label>
                      <input
                        type="date"
                        value={formData.warranty.startDate}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          warranty: {
                            ...prev.warranty,
                            startDate: e.target.value
                          }
                        }))}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                                 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">End Date</label>
                      <input
                        type="date"
                        value={formData.warranty.endDate}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          warranty: {
                            ...prev.warranty,
                            endDate: e.target.value
                          }
                        }))}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                                 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Coverage Type</label>
                      <select
                        value={formData.warranty.coverage}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          warranty: {
                            ...prev.warranty,
                            coverage: e.target.value
                          }
                        }))}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                                 focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="full">Full Coverage</option>
                        <option value="parts-only">Parts Only</option>
                        <option value="labor-only">Labor Only</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Warranty Number</label>
                      <input
                        type="text"
                        value={formData.warranty.warrantyNumber}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          warranty: {
                            ...prev.warranty,
                            warrantyNumber: e.target.value
                          }
                        }))}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                                 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Extended Warranty Option */}
                  <div className="mt-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="extendedWarranty"
                        checked={formData.warranty.extendedWarrantyOption}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          warranty: {
                            ...prev.warranty,
                            extendedWarrantyOption: e.target.checked
                          }
                        }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="extendedWarranty" className="ml-2 block text-sm text-gray-900">
                        Extended Warranty Available
                      </label>
                    </div>
                    {formData.warranty.extendedWarrantyOption && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700">Extended Warranty Cost</label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">$</span>
                          </div>
                          <input
                            type="number"
                            value={formData.warranty.extendedWarrantyCost}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              warranty: {
                                ...prev.warranty,
                                extendedWarrantyCost: e.target.value
                              }
                            }))}
                            className="block w-full rounded-md border border-gray-300 pl-7 pr-12 py-2
                                     focus:border-blue-500 focus:ring-blue-500"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Location & Assignment Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <Building2 className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Location & Assignment</h2>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Location</label>
                  <select
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Select Location</option>
                    {LOCATIONS.map(location => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Technical Specifications Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Wrench className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Technical Specifications</h2>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Dimensions</label>
                  <input
                    type="text"
                    value={formData.specifications.dimensions}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      specifications: { ...prev.specifications, dimensions: e.target.value }
                    }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Weight</label>
                  <input
                    type="text"
                    value={formData.specifications.weight}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      specifications: { ...prev.specifications, weight: e.target.value }
                    }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Power</label>
                  <input
                    type="text"
                    value={formData.specifications.power}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      specifications: { ...prev.specifications, power: e.target.value }
                    }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Capacity</label>
                  <input
                    type="text"
                    value={formData.specifications.capacity}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      specifications: { ...prev.specifications, capacity: e.target.value }
                    }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Operating System</label>
                  <input
                    type="text"
                    value={formData.specifications.operatingSystem}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      specifications: { ...prev.specifications, operatingSystem: e.target.value }
                    }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Custom Fields</label>
                  <input
                    type="text"
                    value={formData.specifications.customFields.join(', ')}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      specifications: { ...prev.specifications, customFields: e.target.value.split(',').map(f => f.trim()) }
                    }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Maintenance & Service Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-2 bg-amber-50 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Maintenance & Service</h2>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Maintenance Frequency</label>
                  <select
                    value={formData.maintenanceFrequency}
                    onChange={(e) => setFormData(prev => ({ ...prev, maintenanceFrequency: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="quarterly">Quarterly</option>
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Maintenance Date</label>
                  <input
                    type="date"
                    value={formData.lastMaintenanceDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastMaintenanceDate: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Next Maintenance Date</label>
                  <input
                    type="date"
                    value={formData.nextMaintenanceDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, nextMaintenanceDate: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Service Provider</label>
                  <input
                    type="text"
                    value={formData.serviceProvider}
                    onChange={(e) => setFormData(prev => ({ ...prev, serviceProvider: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Service Contract</label>
                  <input
                    type="text"
                    value={formData.serviceContract}
                    onChange={(e) => setFormData(prev => ({ ...prev, serviceContract: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Criticality Level</label>
                  <select
                    value={formData.criticalityLevel}
                    onChange={(e) => setFormData(prev => ({ ...prev, criticalityLevel: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Information Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-2 bg-green-50 rounded-lg">
                  <BarChart2 className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Financial Information</h2>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Depreciation Method</label>
                  <select
                    value={formData.depreciationMethod}
                    onChange={(e) => setFormData(prev => ({ ...prev, depreciationMethod: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="straight-line">Straight Line</option>
                    <option value="declining-balance">Declining Balance</option>
                    <option value="units-of-production">Units of Production</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Useful Life</label>
                  <input
                    type="text"
                    value={formData.usefulLife}
                    onChange={(e) => setFormData(prev => ({ ...prev, usefulLife: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Salvage Value</label>
                  <input
                    type="text"
                    value={formData.salvageValue}
                    onChange={(e) => setFormData(prev => ({ ...prev, salvageValue: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Current Value</label>
                  <input
                    type="text"
                    value={formData.currentValue}
                    onChange={(e) => setFormData(prev => ({ ...prev, currentValue: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Cost Center</label>
                  <input
                    type="text"
                    value={formData.costCenter}
                    onChange={(e) => setFormData(prev => ({ ...prev, costCenter: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Budget Code</label>
                  <input
                    type="text"
                    value={formData.budgetCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, budgetCode: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Compliance & Safety Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-2 bg-red-50 rounded-lg">
                  <Shield className="w-5 h-5 text-red-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Compliance & Safety</h2>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Certifications</label>
                  <input
                    type="text"
                    value={formData.certifications.join(', ')}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      certifications: e.target.value.split(',').map(c => c.trim())
                    }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Safety Requirements</label>
                  <input
                    type="text"
                    value={formData.safetyRequirements.join(', ')}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      safetyRequirements: e.target.value.split(',').map(r => r.trim())
                    }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Compliance Status</label>
                  <select
                    value={formData.complianceStatus}
                    onChange={(e) => setFormData(prev => ({ ...prev, complianceStatus: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="compliant">Compliant</option>
                    <option value="non-compliant">Non-Compliant</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Inspection Date</label>
                  <input
                    type="date"
                    value={formData.lastInspectionDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastInspectionDate: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Next Inspection Date</label>
                  <input
                    type="date"
                    value={formData.nextInspectionDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, nextInspectionDate: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2
                             focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Quick Actions & Info */}
        <div className="space-y-6 sticky top-6">
          {/* Preview & Actions Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">Asset Preview</h3>
            </div>
            
            {/* Asset Label Preview */}
            <div className="p-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                {generatedAssetId ? (
                  <div className="space-y-4">
                    {/* QR Code */}
                    <div className="flex justify-center">
                      <QRCode value={getQRCodeData()} size={150} />
                    </div>
                    
                    {/* Asset Details */}
                    <div className="text-center space-y-2">
                      <h4 className="text-sm font-medium text-gray-900">{formData.name || 'Asset Name'}</h4>
                      <p className="text-xs text-gray-500">{formData.category || 'Category'}</p>
                      <div className="bg-blue-50 rounded-md py-1 px-2">
                        <p className="text-xs font-mono text-blue-700">{generatedAssetId}</p>
                      </div>
                    </div>

                    {/* Asset Image Preview */}
                    {formData.images.length > 0 && (
                      <div className="mt-4">
                        <div className="relative rounded-lg overflow-hidden">
                          <img
                            src={URL.createObjectURL(formData.images[0])}
                            alt="Asset"
                            className="w-full h-48 object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, images: [] }))}
                            className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white 
                                     hover:bg-red-600 transition-colors"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <QrCode className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Generate an asset ID to preview label</p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="p-4 space-y-3">
              <button
                type="button"
                onClick={generateAssetNumber}
                className="w-full px-4 py-2 text-sm text-blue-600 border border-blue-200 
                         hover:bg-blue-50 rounded-lg flex items-center justify-center gap-2"
              >
                <QrCode className="w-4 h-4" />
                Generate New ID
              </button>

              {/* Image Upload Button */}
              <label className="w-full px-4 py-2 text-sm text-purple-600 border border-purple-200 
                              hover:bg-purple-50 rounded-lg flex items-center justify-center gap-2
                              cursor-pointer">
                <Camera className="w-4 h-4" />
                {formData.images.length === 0 ? 'Add Asset Image' : 'Change Image'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    if (files.length > 0) {
                      setFormData(prev => ({
                        ...prev,
                        images: [files[0]] // Only keep the latest image
                      }));
                    }
                  }}
                />
              </label>

              {generatedAssetId && (
                <>
                  {/* View Label Button */}
                  <button
                    type="button"
                    onClick={() => {/* Add view label modal logic */}}
                    className="w-full px-4 py-2 text-sm text-indigo-600 border border-indigo-200 
                             hover:bg-indigo-50 rounded-lg flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    View Label
                  </button>

                  {/* Print Label Button */}
                  <button
                    type="button"
                    onClick={() => {/* Add print logic */}}
                    className="w-full px-4 py-2 text-sm text-green-600 border border-green-200 
                             hover:bg-green-50 rounded-lg flex items-center justify-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Print Label
                  </button>
                </>
              )}
            </div>

            {/* Label Templates */}
            <div className="p-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Label Templates</h3>
              <div className="space-y-2">
                <button
                  type="button"
                  className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 
                           rounded-lg flex items-center justify-between group"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-gray-500" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium">Standard Label</p>
                      <p className="text-xs text-gray-500">60mm x 40mm</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                </button>

                <button
                  type="button"
                  className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 
                           rounded-lg flex items-center justify-between group"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-gray-500" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium">Large Label</p>
                      <p className="text-xs text-gray-500">100mm x 60mm</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                </button>
              </div>
            </div>
          </div>

          {/* Help & Guidelines Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Guidelines</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <InfoIcon className="w-4 h-4 text-blue-500 mt-0.5" />
                  <p className="text-sm text-gray-600">Asset IDs are unique identifiers that cannot be changed once generated</p>
                </div>
                <div className="flex items-start gap-2">
                  <InfoIcon className="w-4 h-4 text-blue-500 mt-0.5" />
                  <p className="text-sm text-gray-600">QR codes contain essential asset information for quick scanning</p>
                </div>
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5" />
                  <p className="text-sm text-gray-600">Required fields are marked with *</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Images & Documents Section */}
      <div className="mt-6 space-y-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="p-2 bg-rose-50 rounded-lg">
            <FileText className="w-5 h-5 text-rose-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Images & Documents</h2>
        </div>
        
        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Asset Images</label>
          <div className="flex items-center gap-4">
            {formData.images.map((image, index) => (
              <div key={index} className="relative">
                <img
                  src={URL.createObjectURL(image)}
                  alt={`Asset ${index + 1}`}
                  className="w-24 h-24 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    images: prev.images.filter((_, i) => i !== index)
                  }))}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            ))}
            <label className="w-24 h-24 flex flex-col items-center justify-center border-2 
                            border-dashed border-gray-300 rounded-lg cursor-pointer
                            hover:border-blue-400 transition-colors">
              <Camera className="w-8 h-8 text-gray-400" />
              <span className="mt-1 text-xs text-gray-500">Add Image</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleFileChange(e, 'images')}
              />
            </label>
          </div>
        </div>

        {/* Document Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Documents</label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 
                        border-dashed rounded-lg hover:border-blue-400 transition-colors">
            <div className="space-y-1 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <label className="relative cursor-pointer rounded-md font-medium text-blue-600 
                                hover:text-blue-500">
                  <span>Upload files</span>
                  <input
                    type="file"
                    multiple
                    className="sr-only"
                    onChange={(e) => handleFileChange(e, 'documents')}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs text-gray-500">PDF, DOC up to 10MB</p>
            </div>
          </div>
          
          {/* Document List */}
          {formData.documents.length > 0 && (
            <div className="mt-4 space-y-2">
              {formData.documents.map((doc, index) => (
                <div key={index} className="flex items-center justify-between py-2 px-3 
                                          bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{doc.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      documents: prev.documents.filter((_, i) => i !== index)
                    }))}
                    className="text-red-500 hover:text-red-600"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Submit Buttons */}
      <div className="flex justify-end gap-3 pt-6">
        <button
          type="button"
          onClick={() => navigate('/assets')}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white 
                   border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 
                   rounded-lg hover:bg-blue-700"
        >
          Add Asset
        </button>
      </div>

      {/* Scanner Modal */}
      <ScannerModal
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        target={scanTarget}
        onScan={(value) => {
          setFormData(prev => ({
            ...prev,
            [scanTarget]: value
          }));
          setShowScanner(false);
        }}
      />
    </div>
  );
};

export default AddAssetForm; 